import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';
import cn from 'classnames';
import { Box } from '@material-ui/core';
import Routes from 'routes';
import { ReactComponent as EnvelopeIcon } from 'assets/icons/others/envelope.svg';
import { useAppContext } from 'contexts/AppContext';
import { BillingCycleOption, BillingPlanOption } from 'globals/types';
import { isSuccessState, slugify } from 'globals/utils';
import defaultLocales from 'globals/locales/default';
import FsLargeSwitch from 'ui-elements/form-elements/FsLargeSwitch';
import FsDefaultFallbackComponent from 'ui-elements/loaders/FsDefaultFallbackComponent';
import {
  DashedLine,
  Line,
  OptionCaption,
  OptionDescription,
  OptionLabel,
  OptionMainElementWrapper,
  OptionPrice,
  OptionPriceCurrency,
  OptionPriceLabel,
  OptionPriceWrapper,
  OptionSubTitle,
  OptionsWrapperBox,
  OptionTitle,
  SingleOptionBox,
  Special,
  Strong,
  SwitchWrapper,
  Title,
  TriggerButton,
} from './UpgradeContent.style-elements';
import { useStyles } from './UpgradeContent.style';
import { getMinimumSpending, getSymbol } from './UpgradeContext.utils';

interface UpgradeContentProps {
  onClose: () => void;
}

const UpgradeContent: React.FC<UpgradeContentProps> = ({ onClose }) => {
  const {
    upgradeDetails: { status, getDetails, init: initStore },
  } = useAppContext();
  const history = useHistory();
  const {
    activeLabel: activeLabelClass,
    activeStrong: activeStrongClass,
  } = useStyles();
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const details = getDetails();
  const pricingData = isChecked ? details.yearly : details.monthly;
  const minimumSpendAmount = getMinimumSpending(pricingData?.minimumSpend);
  const personalCurrencySymbol = getSymbol(pricingData?.personal);
  const businessCurrencySymbol = getSymbol(pricingData?.business);

  const redirect = (
    cycle: BillingCycleOption,
    plan: BillingPlanOption
  ): void => {
    history.push({
      pathname: `${Routes.UPGRADE}/${slugify(cycle)}/${slugify(plan)}`,
    });
  };

  const handleClick = (plan: BillingPlanOption) => {
    const cycle = isChecked
      ? BillingCycleOption.YEARLY
      : BillingCycleOption.MONTHLY;
    redirect(cycle, plan);
    onClose();
  };

  useEffect(() => {
    initStore();
  }, [initStore]);

  if (!isSuccessState(status))
    return (
      <FsDefaultFallbackComponent
        status={status}
        data-test="default-fallback-component"
      />
    );

  return (
    <Box>
      <Title>{defaultLocales.upgradeHeadline}</Title>

      {/* Main Switch */}
      <SwitchWrapper>
        <OptionLabel className={cn({ [activeLabelClass]: !isChecked })}>
          {defaultLocales.billMonthly}
        </OptionLabel>
        <FsLargeSwitch
          checked={isChecked}
          onChange={() => setIsChecked((currentValue) => !currentValue)}
        />
        <OptionLabel className={cn({ [activeLabelClass]: isChecked })}>
          {defaultLocales.billYearly}{' '}
          <Strong className={cn({ [activeStrongClass]: isChecked })}>
            {defaultLocales.billYearlySaveInfo}
          </Strong>
        </OptionLabel>
      </SwitchWrapper>

      {/* Option: Pay as you grow */}
      <OptionsWrapperBox>
        <SingleOptionBox isFirst>
          <Box mb={2}>
            <OptionTitle>
              {defaultLocales.enums[BillingPlanOption.PAY_AS_YOU_GROW]}
            </OptionTitle>
            <Line />
            <OptionSubTitle>{defaultLocales.startsFrom}</OptionSubTitle>
          </Box>

          <Box>
            <DashedLine />
            <OptionMainElementWrapper>
              <OptionCaption>
                <strong>{defaultLocales.personal}</strong>{' '}
                {defaultLocales.bankAccounts}
              </OptionCaption>
              <OptionPriceWrapper>
                <OptionPrice>
                  <OptionPriceCurrency>
                    {personalCurrencySymbol}
                  </OptionPriceCurrency>
                  {pricingData?.personal?.value}
                </OptionPrice>
                <OptionPriceLabel>
                  /{defaultLocales.customer.toLowerCase()}
                </OptionPriceLabel>
              </OptionPriceWrapper>
            </OptionMainElementWrapper>
            <DashedLine />
            <OptionMainElementWrapper>
              <OptionCaption>
                <strong>{defaultLocales.business}</strong>{' '}
                {defaultLocales.bankAccounts}
              </OptionCaption>
              <OptionPriceWrapper>
                <OptionPrice>
                  <OptionPriceCurrency>
                    {businessCurrencySymbol}
                  </OptionPriceCurrency>
                  {pricingData?.business?.value}
                </OptionPrice>
                <OptionPriceLabel>
                  /{defaultLocales.customer.toLowerCase()}
                </OptionPriceLabel>
              </OptionPriceWrapper>
            </OptionMainElementWrapper>
            <DashedLine mb={2} />
            <OptionDescription>
              {defaultLocales.payAsYouGrowSection.info.prefix}
              <br />
              <Special>{minimumSpendAmount}</Special>
              {defaultLocales.payAsYouGrowSection.info.suffix}
            </OptionDescription>
          </Box>

          <TriggerButton
            onClick={() => handleClick(BillingPlanOption.PAY_AS_YOU_GROW)}
          >
            {defaultLocales.choose}
          </TriggerButton>
        </SingleOptionBox>

        {/* Option: Enterprise */}
        <SingleOptionBox>
          <Box mb={2}>
            <OptionTitle>
              {defaultLocales.enums[BillingPlanOption.ENTERPRISE]}
            </OptionTitle>
            <Line />
            <OptionSubTitle>
              {defaultLocales.enterpriseSection.subTitle}
            </OptionSubTitle>
          </Box>

          <Box>
            <OptionMainElementWrapper mb={2}>
              <EnvelopeIcon />
            </OptionMainElementWrapper>
            <OptionDescription>
              {defaultLocales.enterpriseSection.description}
            </OptionDescription>
          </Box>

          <TriggerButton
            onClick={() => handleClick(BillingPlanOption.ENTERPRISE)}
          >
            {defaultLocales.choose}
          </TriggerButton>
        </SingleOptionBox>
      </OptionsWrapperBox>
    </Box>
  );
};

export default observer(UpgradeContent);
